import dayjs from "dayjs";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useStore } from "../../../helpers";
import {
  CANVAS_LIST_DEFAULT_LIMIT,
  TARS_CANVAS_LIST_PATH,
  TARS_FACETS_LIST_PATH,
  TARS_NOTEBOOK_CARD_DATE_FORMAT,
  TARS_NOTEBOOK_CARD_UNKNOWN_DATE,
  TARS_NOTEBOOKS_LIST_STALE_MS,
} from "../tars-constants";

export interface NotebookListItem {
  id: number;
  name: string;
  tags?: string[];
  owner?: number;
  user_id?: number;
  icon?: string;
  more?: Record<string, unknown>;
  createdat?: string;
  modifiedat?: string;
  modified_at?: string;
  updated_at?: string;
  canvas_count?: number;
  canvases_count?: number;
  facet_count?: number;
  facets_count?: number;
  [key: string]: unknown;
}

export interface NotebooksListResponse {
  notebooks?: NotebookListItem[];
  [key: string]: unknown;
}

export interface NotebookCardStats {
  canvasCount: number;
  facetCount: number;
}

export const TARS_NOTEBOOKS_LIST_QUERY_KEY = ["notebooks"] as const;

const MODIFIED_DATE_KEYS = [
  "modifiedat",
  "modified_at",
  "modifiedAt",
  "updatedat",
  "updated_at",
  "updatedAt",
  "last_modified",
  "lastModified",
] as const;

export {
  invalidateTarsNotebooksList,
  markTarsNotebookActivity,
  readTarsNotebookLastTouched,
  useTarsNotebookLastTouchedMap,
} from "./tars-notebook-last-touched";

function readCount(raw: unknown): number | null {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim() !== "") {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function pickCount(notebook: NotebookListItem, keys: string[]): number | null {
  for (const key of keys) {
    const direct = readCount(notebook[key]);
    if (direct != null) return direct;
    const fromMore = readCount(notebook.more?.[key]);
    if (fromMore != null) return fromMore;
  }
  return null;
}

function pickModifiedRaw(
  notebook: NotebookListItem,
  detailModifiedat?: string | null
): unknown {
  for (const key of MODIFIED_DATE_KEYS) {
    const direct = notebook[key];
    if (direct != null && direct !== "") return direct;
    const fromMore = notebook.more?.[key];
    if (fromMore != null && fromMore !== "") return fromMore;
  }
  if (detailModifiedat != null && detailModifiedat !== "") return detailModifiedat;
  return undefined;
}

function parseNotebookDate(raw: unknown): dayjs.Dayjs | null {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const ms = raw > 1e12 ? raw : raw * 1000;
    const parsed = dayjs(ms);
    return parsed.isValid() ? parsed : null;
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const parsed = dayjs(trimmed);
    return parsed.isValid() ? parsed : null;
  }
  return null;
}

function pickLatestDate(
  ...dates: Array<dayjs.Dayjs | null | undefined>
): dayjs.Dayjs | null {
  const valid = dates.filter((date): date is dayjs.Dayjs => Boolean(date?.isValid()));
  if (valid.length === 0) return null;
  return valid.reduce((latest, current) => (current.isAfter(latest) ? current : latest));
}

function countFromBody(body: Record<string, unknown>, arrayKeys: string[]): number {
  const total = body.total ?? body.count;
  if (typeof total === "number" && Number.isFinite(total)) return total;
  for (const key of arrayKeys) {
    const value = body[key];
    if (Array.isArray(value)) return value.length;
  }
  return 0;
}

async function fetchNotebookCardStats(
  apiRequestHandler: (
    path: string,
    body: Record<string, unknown>,
    errorMessage: string
  ) => Promise<{ data?: unknown }>,
  notebookId: number
): Promise<NotebookCardStats> {
  const [canvasRes, facetRes] = await Promise.all([
    apiRequestHandler(
      TARS_CANVAS_LIST_PATH,
      { notebook_id: notebookId, offset: 0, limit: CANVAS_LIST_DEFAULT_LIMIT },
      "Failed to fetch canvas count"
    ),
    apiRequestHandler(
      TARS_FACETS_LIST_PATH,
      { notebook_id: notebookId, offset: 0, limit: CANVAS_LIST_DEFAULT_LIMIT },
      "Failed to fetch facet count"
    ),
  ]);
  const canvasBody = (canvasRes.data ?? canvasRes) as Record<string, unknown>;
  const facetBody = (facetRes.data ?? facetRes) as Record<string, unknown>;
  return {
    canvasCount: countFromBody(canvasBody, ["canvases"]),
    facetCount: countFromBody(facetBody, ["facets", "items"]),
  };
}

export function notebookLastUpdatedLabel(
  notebook: NotebookListItem,
  options?: { detailModifiedat?: string | null; lastTouchedMs?: number | null }
): string {
  const apiDate = parseNotebookDate(pickModifiedRaw(notebook, options?.detailModifiedat));
  const touchedDate =
    options?.lastTouchedMs != null && Number.isFinite(options.lastTouchedMs)
      ? dayjs(options.lastTouchedMs)
      : null;
  const picked = pickLatestDate(apiDate, touchedDate);
  if (!picked) return TARS_NOTEBOOK_CARD_UNKNOWN_DATE;
  return picked.format(TARS_NOTEBOOK_CARD_DATE_FORMAT);
}

export const useGetNotebooks = () => {
  const rootStore = useStore();
  return useQuery({
    queryKey: TARS_NOTEBOOKS_LIST_QUERY_KEY,
    queryFn: async () => {
      const res = await rootStore.apiRequestHandler(
        "tars/notebooks/list",
        {},
        "Failed to fetch notebooks"
      );
      return res.data as NotebooksListResponse;
    },
    staleTime: TARS_NOTEBOOKS_LIST_STALE_MS,
    refetchOnMount: "always",
  });
};

export function useNotebookCardStatsMap(notebooks: NotebookListItem[]) {
  const rootStore = useStore();
  const needsFetch = notebooks.filter(
    (notebook) =>
      pickCount(notebook, ["canvas_count", "canvases_count", "canvasCount"]) == null ||
      pickCount(notebook, ["facet_count", "facets_count", "facetCount"]) == null
  );
  const queries = useQueries({
    queries: needsFetch.map((notebook) => ({
      queryKey: ["tars", "notebook", "card-stats", notebook.id],
      queryFn: () => fetchNotebookCardStats(rootStore.apiRequestHandler.bind(rootStore), notebook.id),
      staleTime: 60_000,
    })),
  });
  const fetchedById = new Map<number, NotebookCardStats>();
  needsFetch.forEach((notebook, index) => {
    const data = queries[index]?.data;
    if (data) fetchedById.set(notebook.id, data);
  });
  const statsById = new Map<number, NotebookCardStats>();
  for (const notebook of notebooks) {
    statsById.set(notebook.id, {
      canvasCount:
        pickCount(notebook, ["canvas_count", "canvases_count", "canvasCount"]) ??
        fetchedById.get(notebook.id)?.canvasCount ??
        0,
      facetCount:
        pickCount(notebook, ["facet_count", "facets_count", "facetCount"]) ??
        fetchedById.get(notebook.id)?.facetCount ??
        0,
    });
  }
  return { statsById, isLoading: queries.some((query) => query.isLoading) };
}
